import { defaultAllSelection } from "services/constants/selections";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { theme, optionalAvathar } from "services/constants";
import { useSelector } from "react-redux";
import {
  EmptyState,
  TableEmptyState,
} from "components/CustomComponents/EmptyState";
import {
  checkViewerPermission,
  subModuleNameMatches,
} from "services/utils/HelperFunction";
import { Box } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { FilterProvider } from "pages/FilterContext";
import { SearchRounded, ScheduleRounded } from "@mui/icons-material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import Groups2Rounded from "@mui/icons-material/Groups2Rounded";
import PinRounded from "@mui/icons-material/PinRounded";
import { punchDetailsHeader } from "../ReportHeaders";
import { showToast } from "services/utils/Status";
import sessionHandling from "services/utils/notificationUtils";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { CompactHeaderSelectFilter } from "components/CustomComponents/CompactHeaderSelectFilter";
import { useExcelDownload } from "components/CustomComponents/useExcelDownload";
import {
  APP_BAR_OFFSET,
  compactRowPager,
  designColors,
  designShell,
  paginationFooter,
  rowsPerPageOptions,
  StatCard,
  withDesignFont,
} from "services/constants/designTokens";
import {
  approveStickyCustomFilterBar,
  approveStickyFooterBar,
  approveTableBodyCell,
  approveTableHeadCell,
  ApproveTable,
} from "pages/TeamZone/Approve/approveUi";

const ADJUSTMENT_REPORT_MODE = "adjustment";
const EMPLOYEE_WISE_MODE = "employee-wise";
const DATE_WISE_MODE = "date-wise";
const EXPAND_TOGGLE_OPTIONS = [
  { id: "collapse-all", label: "Collapse All" },
  { id: "expand-all", label: "Expand All" },
];
// Fixed page size for a single expanded row's punch/adjustment list — a
// busy date can embed thousands of entries even though the outer table is
// paginated at 25, so this pages that inner list independently.
const PUNCH_DETAIL_PAGE_SIZE = 25;
// Date-wise outer table is bounded to a date range's worth of rows rather
// than a whole employee roster, so a smaller page size than the app-wide
// default reads better.
const DATE_WISE_ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const getDisplayValue = (value, fallback = "-") =>
  value || value === 0 ? value : fallback;

const hasDisplayValue = (value) =>
  value !== undefined && value !== null && value !== "" && value !== "-";

const getEmbeddedPunchRows = (row) => {
  if (!row || typeof row !== "object") {
    return { hasEmbeddedRows: false, rows: [] };
  }

  if (Object.prototype.hasOwnProperty.call(row, "punches")) {
    return {
      hasEmbeddedRows: true,
      rows: Array.isArray(row?.punches) ? row.punches : [],
    };
  }

  if (Object.prototype.hasOwnProperty.call(row, "data")) {
    return {
      hasEmbeddedRows: true,
      rows: Array.isArray(row?.data) ? row.data : [],
    };
  }

  if (Object.prototype.hasOwnProperty.call(row, "punch_details")) {
    return {
      hasEmbeddedRows: true,
      rows: Array.isArray(row?.punch_details) ? row.punch_details : [],
    };
  }

  return { hasEmbeddedRows: false, rows: [] };
};

const parsePunchDate = (value) => {
  if (!value) {
    return null;
  }

  const strictParsedDate = moment(
    value,
    ["YYYY-MM-DD", "DD/MM/YYYY", "DD-MM-YYYY", moment.ISO_8601],
    true,
  );
  if (strictParsedDate.isValid()) {
    return strictParsedDate;
  }

  const relaxedParsedDate = moment(value);
  return relaxedParsedDate.isValid() ? relaxedParsedDate : null;
};

const normalizePunchDateKey = (value) => {
  const parsedDate = parsePunchDate(value);
  return parsedDate ? parsedDate.format("YYYY-MM-DD") : "";
};

const formatPunchDateLabel = (value) => {
  const dateKey = normalizePunchDateKey(value);
  return dateKey ? moment(dateKey, "YYYY-MM-DD").format("DD/MM/YYYY") : "-";
};

export const getAdjustmentModifiedAt = (punchRow) =>
  getDisplayValue(
    punchRow?.modified_at ||
      punchRow?.modifiedat ||
      punchRow?.modified_at_formatted ||
      punchRow?.modified_on ||
      punchRow?.updated_at,
  );

export const getAdjustmentModifiedBy = (punchRow) =>
  getDisplayValue(
    punchRow?.modified_by ||
      punchRow?.modified_by_name ||
      punchRow?.modified_username ||
      punchRow?.updated_by,
  );

export const isAdjustmentPunchRow = (punchRow) => {
  const punchTime = punchRow?.punch_time;
  const attendanceTime = punchRow?.attendance_time;
  const manualDeleteValue =
    punchRow?.is_manual_delete ?? punchRow?.is_manually_deleted;
  const hasTimeAdjustment =
    hasDisplayValue(punchTime) &&
    hasDisplayValue(attendanceTime) &&
    `${punchTime}` !== `${attendanceTime}`;

  return (
    hasTimeAdjustment ||
    hasDisplayValue(getAdjustmentModifiedBy(punchRow)) ||
    hasDisplayValue(getAdjustmentModifiedAt(punchRow)) ||
    manualDeleteValue === true ||
    `${manualDeleteValue}`.toLowerCase() === "yes"
  );
};

const adjustmentReportHeader = [
  ...punchDetailsHeader.slice(0, 2),
  {
    name: "Department Name",
    key: "department_name",
    type: "autocomplete",
    icon: <Groups2Rounded fontSize="small" />,
  },
  punchDetailsHeader.find((header) => header.key === "punch_in_count"),
].filter(Boolean);

const punchDetailsColumns = [
  { header: "Date", value: (row) => getDisplayValue(row?.date) },
  { header: "Shift", value: (row) => getDisplayValue(row?.shift_name) },
  { header: "Punch in", value: (row) => getDisplayValue(row?.punch_time) },
  {
    header: "Attendance Time",
    value: (row) => getDisplayValue(row?.attendance_time),
  },
  { header: "Direction", value: (row) => getDisplayValue(row?.direction) },
  { header: "Device name", value: (row) => getDisplayValue(row?.device_name) },
  { header: "Location", value: (row) => getDisplayValue(row?.location) },
  { header: "Modified by", value: (row) => getAdjustmentModifiedBy(row) },
  {
    header: "Is Manually Deleted",
    value: (row) => (row?.is_manual_delete ? "Yes" : "No"),
  },
];

const adjustmentReportColumns = [
  { header: "Date", value: (row) => getDisplayValue(row?.date) },
  { header: "Shift", value: (row) => getDisplayValue(row?.shift_name) },
  {
    header: "Original Time",
    value: (row) => getDisplayValue(row?.punch_time),
  },
  {
    header: "Adjusted Time",
    value: (row) => getDisplayValue(row?.attendance_time),
  },
  { header: "Modified by", value: (row) => getAdjustmentModifiedBy(row) },
  { header: "Modified At", value: (row) => getAdjustmentModifiedAt(row) },
];

const dateWisePunchSummaryHeaders = [
  {
    name: "Date",
    key: "date",
    type: "date",
    icon: <ScheduleRounded fontSize="small" />,
  },
  {
    name: "Punch Count",
    key: "punch_in_count",
    type: "number",
    icon: <PinRounded fontSize="small" />,
  },
];

const dateWiseAdjustmentSummaryHeaders = [
  {
    name: "Date",
    key: "date",
    type: "date",
    icon: <ScheduleRounded fontSize="small" />,
  },
  {
    name: "Adjustment Count",
    key: "punch_in_count",
    type: "number",
    icon: <PinRounded fontSize="small" />,
  },
];

const dateWisePunchExpandedColumns = [
  { header: "Employee Name", value: (row) => getDisplayValue(row?.username) },
  { header: "Employee Id", value: (row) => getDisplayValue(row?.employee_id) },
  {
    header: "Department Name",
    value: (row) => getDisplayValue(row?.department_name),
  },
  ...punchDetailsColumns.filter((column) => column.header !== "Date"),
];

const dateWiseAdjustmentExpandedColumns = [
  { header: "Employee Name", value: (row) => getDisplayValue(row?.username) },
  { header: "Employee Id", value: (row) => getDisplayValue(row?.employee_id) },
  {
    header: "Department Name",
    value: (row) => getDisplayValue(row?.department_name),
  },
  ...adjustmentReportColumns.filter((column) => column.header !== "Date"),
];

// Mirrors TimeCardReport.js's own local drill-down table pattern — a
// bordered, rounded scroll box with a sticky slate-100 head — kept local
// here for the same reason (this file and TimeCardReport don't import from
// each other).
const expandedPunchTableContainerSx = (maxHeight) => ({
  marginTop: 0,
  marginLeft: "auto",
  marginRight: "auto",
  position: "relative",
  width: "100%",
  maxHeight,
  overflow: "auto",
  border: `1px solid ${designColors.slate200}`,
  borderRadius: 2,
  backgroundColor: designColors.white,
  // Rows become taller cards below 900px, so drop the fixed row-count height.
  "@media (max-width:899.95px)": { maxHeight: "none", overflow: "visible" },
});

const expandedPunchTableSx = {
  "& .MuiTableHead-root": {
    ...designShell.tableStickyHead,
    zIndex: 0,
  },
  "& .MuiTableHead-root .MuiTableRow-root": {
    ...designShell.tableStickyHeadRow,
    zIndex: 0,
  },
  "& .MuiTableHead-root .MuiTableCell-root": {
    ...approveTableHeadCell,
    top: 0,
    zIndex: 1,
    backgroundColor: designColors.slate100,
  },
  "& .MuiTableBody-root .MuiTableCell-root": approveTableBodyCell,
  "& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root": {
    borderBottom: "none",
  },
};

export const PunchDetails = ({
  punchDetailsData,
  viewMode = EMPLOYEE_WISE_MODE,
  departmentId,
  startDate,
  endDate,
  setRemoveExpand,
  removeExpand,
  page,
  setLeavePage,
  setLeaveRowsPerPage,
  rowsPerPage,
  hideCount = false,
  metadata,
  mode = "punch",
  onVisibleHeadersChange,
}) => {
  const { domain, token, topbarCompanyId, userScreenAccess, userDetails } =
    useSelector((state) => state.tokenReducer);
  const [personExpandableUserId, setPersonExpandableUserId] = useState(null);
  const [expandMode, setExpandMode] = useState("collapse-all");
  const [expandedUserIds, setExpandedUserIds] = useState([]);
  const [expandedPunchData, setExpandedPunchData] = useState({});
  const [expandedPunchDataPageByKey, setExpandedPunchDataPageByKey] = useState(
    {},
  );
  // Date-wise only: lets a user search within one expanded date's employee
  // list instead of paging through a busy date 25 at a time. Employee-wise
  // expanded rows are just that employee's own punches across the date
  // range (naturally small), so search isn't offered there.
  const [expandedPunchDataSearchByKey, setExpandedPunchDataSearchByKey] =
    useState({});
  const [expandedPunchLoadingUserIds, setExpandedPunchLoadingUserIds] =
    useState([]);
  const [punchData, setPunchData] = useState(null);
  // Tracks which sourceRows punchData was actually computed from, so a
  // view-mode / punch-vs-adjustment toggle (or fresh data landing) doesn't
  // render one frame of the previous filter result (or an empty table)
  // before CustomFilter's own effect catches up — same pattern as
  // TimeCardReport.js.
  const punchDataSourceRef = useRef(null);
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);
  const footerRef = useCallback((node) => setFooterNode(node), []);
  const {
    downloadCsv,
    downloadExcel,
    downloadPdf,
    downloadGroupedExcel,
    downloadGroupedCsv,
    downloadGroupedPdf,
  } = useExcelDownload();
  const isAdjustmentReport = mode === ADJUSTMENT_REPORT_MODE;
  const isDateWiseView = viewMode === DATE_WISE_MODE;
  const filterHeaders = isAdjustmentReport
    ? adjustmentReportHeader
    : punchDetailsHeader;
  const employeeWiseExpandedColumns = isAdjustmentReport
    ? adjustmentReportColumns
    : punchDetailsColumns;
  const reportTitle = isAdjustmentReport ? "Adjustment Entry" : "Punch Entry";

  const getExpandedTableHeight = useCallback((rowCount) => {
    if (!rowCount) return 170;

    const headerHeight = 56;
    const rowHeight = 44;
    const bufferHeight = 12;

    return Math.min(headerHeight + rowCount * rowHeight + bufferHeight, 420);
  }, []);

  const teamZoneReports = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Reports",
  );

  const subModuleId = teamZoneReports?.submodules?.find((sub) =>
    subModuleNameMatches(sub.sub_module_name, "Punch Details"),
  )?.sub_module_id;

  const check_access = checkViewerPermission(
    userScreenAccess?.module_details,
    "Reports",
    isAdjustmentReport ? "Adjustment Entry" : "Punch Entry",
  );

  // Punch Entry and Adjustment Entry are two distinct sets of columns behind
  // one "Punch Details" subModuleId, so they get their own scope each
  // (same shape as Attendance/Reports' department_view/shift_view split)
  // rather than sharing one saved-view bucket.
  const handleSavedViewChange = useCallback(() => {
    setLeavePage(0);
  }, [setLeavePage]);

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "punch_details_saved_views",
    scope: isAdjustmentReport ? "adjustment_entry" : "punch_entry",
    fullHeaders: filterHeaders,
    screenAccess: check_access,
    onViewChange: handleSavedViewChange,
  });

  // The Adjustment Entry download is a custom drill-down export owned by the
  // parent (it fetches per-employee adjustment rows the summary table never
  // loads), so it can't read this component's own useSavedViewSelector state
  // directly — surface the currently visible headers up so that export can
  // be kept in sync with whatever the user picked here instead of always
  // exporting every identity column regardless of the active saved view.
  useEffect(() => {
    onVisibleHeadersChange?.(visibleHeaders);
  }, [onVisibleHeadersChange, visibleHeaders]);

  useEffect(() => {
    if (removeExpand) {
      setPersonExpandableUserId(null);
      setExpandMode("collapse-all");
      setExpandedUserIds([]);
      setExpandedPunchData({});
      setExpandedPunchDataPageByKey({});
      setExpandedPunchDataSearchByKey({});
      setRemoveExpand(false);
    }
  }, [removeExpand, setRemoveExpand]);

  const dateWiseRows = useMemo(() => {
    const rowsByDate = new Map();

    (punchDetailsData || []).forEach((employeeRow) => {
      const identity = {
        user_id: employeeRow?.user_id,
        username:
          employeeRow?.username ||
          employeeRow?.employee_name ||
          employeeRow?.user_name,
        employee_id: employeeRow?.employee_id,
        department_name: employeeRow?.department_name,
        image_url: employeeRow?.image_url ?? optionalAvathar,
      };
      const { rows } = getEmbeddedPunchRows(employeeRow);
      const relevantRows = isAdjustmentReport
        ? rows.filter(isAdjustmentPunchRow)
        : rows;

      relevantRows.forEach((punchRow) => {
        const dateKey = normalizePunchDateKey(punchRow?.date);
        if (!dateKey) {
          return;
        }

        if (!rowsByDate.has(dateKey)) {
          rowsByDate.set(dateKey, {
            row_id: `date:${dateKey}`,
            date_key: dateKey,
            date: formatPunchDateLabel(dateKey),
            employeeIds: new Set(),
            punchRows: [],
          });
        }

        const groupedRow = rowsByDate.get(dateKey);
        groupedRow.employeeIds.add(
          `${identity.user_id || identity.employee_id || identity.username || ""}`,
        );
        groupedRow.punchRows.push({
          ...punchRow,
          ...identity,
          date: formatPunchDateLabel(dateKey),
        });
      });
    });

    const normalizedStartDate = normalizePunchDateKey(startDate);
    const normalizedEndDate = normalizePunchDateKey(endDate);
    const startMoment = parsePunchDate(normalizedStartDate || startDate);
    const endMoment = parsePunchDate(normalizedEndDate || endDate);

    if (startMoment && endMoment && !startMoment.isAfter(endMoment, "day")) {
      const rangeRows = [];
      const cursor = startMoment.clone();

      while (!cursor.isAfter(endMoment, "day")) {
        const dateKey = cursor.format("YYYY-MM-DD");
        const existingRow = rowsByDate.get(dateKey);

        rangeRows.push({
          row_id: `date:${dateKey}`,
          date_key: dateKey,
          date: cursor.format("DD/MM/YYYY"),
          employee_count: existingRow ? existingRow.employeeIds.size : 0,
          punch_in_count: existingRow ? existingRow.punchRows.length : 0,
          punch_rows: existingRow ? existingRow.punchRows : [],
        });

        cursor.add(1, "day");
      }

      return rangeRows;
    }

    return Array.from(rowsByDate.values())
      .sort((firstRow, secondRow) =>
        firstRow.date_key.localeCompare(secondRow.date_key),
      )
      .map((row) => ({
        row_id: row.row_id,
        date_key: row.date_key,
        date: row.date,
        employee_count: row.employeeIds.size,
        punch_in_count: row.punchRows.length,
        punch_rows: row.punchRows,
      }));
  }, [endDate, isAdjustmentReport, punchDetailsData, startDate]);

  const activeFilterHeaders = isDateWiseView
    ? isAdjustmentReport
      ? dateWiseAdjustmentSummaryHeaders
      : dateWisePunchSummaryHeaders
    : visibleHeaders;

  const activeTitleOverride = isDateWiseView ? null : titleOverride;

  const sourceRows = isDateWiseView ? dateWiseRows : punchDetailsData;
  const punchDataReady = punchDataSourceRef.current === sourceRows;
  const visiblePunchData = useMemo(
    () => (punchData !== null && punchDataReady ? punchData : sourceRows || []),
    [punchData, punchDataReady, sourceRows],
  );

  const activeExpandedColumns = isDateWiseView
    ? isAdjustmentReport
      ? dateWiseAdjustmentExpandedColumns
      : dateWisePunchExpandedColumns
    : employeeWiseExpandedColumns;

  const hasSummarizedDownloadData = useMemo(
    () =>
      (visiblePunchData || []).some(
        (row) =>
          Number(row?.punch_in_count || 0) > 0 ||
          Number(row?.employee_count || 0) > 0,
      ),
    [visiblePunchData],
  );

  const hasDetailedDownloadData = useMemo(
    () =>
      (visiblePunchData || []).some((row) => {
        const detailRows = isDateWiseView
          ? row?.punch_rows || []
          : getEmbeddedPunchRows(row).rows || [];

        return isAdjustmentReport
          ? detailRows.some(isAdjustmentPunchRow)
          : detailRows.length > 0;
      }),
    [isAdjustmentReport, isDateWiseView, visiblePunchData],
  );

  // Mirrors handleDetailedDownload's own groups.reduce((sum, g) => sum + 1 +
  // g.detailRows.length) row count exactly, so the pre-download PDF
  // disable/hover check reflects what would actually be exported instead of
  // just the summary row count — computed locally rather than passed down,
  // since the parent's working-hours fetch never populates data for
  // punch-based report types (see isPunchBasedReport), which used to leave
  // the Adjustment view's row count reading stale, unrelated data.
  const detailedExportRowCount = useMemo(
    () =>
      (visiblePunchData || []).reduce((sum, row) => {
        const detailRows = isDateWiseView
          ? row?.punch_rows || []
          : getEmbeddedPunchRows(row).rows || [];
        return sum + 1 + detailRows.length;
      }, 0),
    [isDateWiseView, visiblePunchData],
  );

  const getTopLevelRowKey = useCallback(
    (row) =>
      isDateWiseView
        ? row?.row_id || `date:${row?.date_key || row?.date || ""}`
        : `${row?.user_id || row?.employee_id || ""}`,
    [isDateWiseView],
  );

  const triggerDownloadByFormat = useCallback(
    async (
      rows,
      headers,
      filename,
      metadataOverride,
      filterValues = {},
      sortRules = [],
      downloadFormat = "excel",
      onProgress,
    ) => {
      if (downloadFormat === "pdf") {
        await downloadPdf(
          rows,
          headers,
          filename,
          filterValues,
          metadataOverride,
          sortRules,
          onProgress,
        );
        return;
      }
      if (downloadFormat === "csv") {
        await downloadCsv(
          rows,
          headers,
          filename,
          filterValues,
          metadataOverride,
          sortRules,
          onProgress,
        );
        return;
      }
      await downloadExcel(
        rows,
        headers,
        filename,
        filterValues,
        metadataOverride,
        sortRules,
        onProgress,
      );
    },
    [downloadCsv, downloadExcel, downloadPdf],
  );

  const getExpandedPunches = useCallback(
    async (selectedUserId, sourceRowArg = null) => {
      if (isDateWiseView) {
        const rowKey = getTopLevelRowKey(sourceRowArg);
        const dateRows = Array.isArray(sourceRowArg?.punch_rows)
          ? sourceRowArg.punch_rows
          : [];
        setExpandedPunchData((prev) => ({
          ...prev,
          [rowKey]: dateRows,
        }));
        return;
      }

      const sourceRow =
        sourceRowArg ||
        visiblePunchData?.find(
          (row) => `${row?.user_id || ""}` === `${selectedUserId}`,
        ) ||
        punchDetailsData?.find(
          (row) => `${row?.user_id || ""}` === `${selectedUserId}`,
        );
      const { hasEmbeddedRows, rows } = getEmbeddedPunchRows(sourceRow);

      if (hasEmbeddedRows) {
        setExpandedPunchData((prev) => ({
          ...prev,
          [selectedUserId]: rows,
        }));
        return;
      }

      setExpandedPunchLoadingUserIds((prev) =>
        prev.includes(selectedUserId) ? prev : [...prev, selectedUserId],
      );
      try {
        const params = new URLSearchParams({
          user_id: `${userDetails.id}`,
          shift_id: String(defaultAllSelection),
          department_id: `${departmentId}`,
          start_date: startDate,
          end_date: endDate,
          company_id: `${topbarCompanyId}`,
          selected_user_id: `${selectedUserId}`,
        });
        if (isAdjustmentReport) {
          params.set("api_type", "adjustment");
        }

        const response = await fetch(
          `${domain}punchin-details/?${params.toString()}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();
        if (response.ok) {
          const selectedUserPunches = Array.isArray(res)
            ? res.find(
                (item) => `${item?.user_id || ""}` === `${selectedUserId}`,
              )
            : res;

          setExpandedPunchData((prev) => ({
            ...prev,
            [selectedUserId]:
              selectedUserPunches?.punches ||
              selectedUserPunches?.data ||
              selectedUserPunches?.punch_details ||
              [],
          }));
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error(res.error);
        }
      } catch (error) {
        setExpandedPunchData((prev) => ({
          ...prev,
          [selectedUserId]: [],
        }));
        showToast("error", error.message);
      } finally {
        setExpandedPunchLoadingUserIds((prev) =>
          prev.filter((userId) => userId !== selectedUserId),
        );
      }
    },
    [
      departmentId,
      domain,
      endDate,
      getTopLevelRowKey,
      isDateWiseView,
      isAdjustmentReport,
      visiblePunchData,
      punchDetailsData,
      startDate,
      token,
      topbarCompanyId,
      userDetails.id,
    ],
  );

  const handleChangeRowsPerPage = (event) => {
    setLeaveRowsPerPage(parseInt(event.target.value, 10));
    setLeavePage(0);
  };

  const paginatedRows = useMemo(
    () =>
      visiblePunchData?.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ) || [],
    [page, visiblePunchData, rowsPerPage],
  );

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      punchDataSourceRef.current = sourceRows;
      setPunchData(filteredData);
      if (page * rowsPerPage >= filteredData.length) setLeavePage(0);
    },
    [page, rowsPerPage, setLeavePage, sourceRows],
  );

  useEffect(() => {
    if (page * rowsPerPage >= (sourceRows?.length || 0)) {
      setLeavePage(0);
    }
  }, [page, rowsPerPage, setLeavePage, sourceRows]);

  useEffect(() => {
    if (!customFilterNode) return undefined;
    const updateHeight = () =>
      setCustomFilterHeight(customFilterNode.offsetHeight || 0);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode, visibleHeaders.length]);

  useEffect(() => {
    if (!footerNode) return undefined;
    const updateHeight = () => setFooterHeight(footerNode.offsetHeight || 0);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);

  useEffect(() => {
    if (expandMode !== "expand-all") return;

    const nextExpandedIds = paginatedRows
      .map((row) => getTopLevelRowKey(row))
      .filter(Boolean);
    setExpandedUserIds(nextExpandedIds);

    // Batch every row whose punch data is already embedded in the loaded
    // dataset into one state update instead of one setExpandedPunchData call
    // per row — with thousands of rows loaded, that per-row update pattern
    // was what hung the tab on Expand All.
    const embeddedPatch = {};
    const rowsNeedingFetch = [];
    paginatedRows.forEach((row) => {
      const rowKey = getTopLevelRowKey(row);
      if (!rowKey || expandedPunchData[rowKey] !== undefined) return;

      if (isDateWiseView) {
        embeddedPatch[rowKey] = Array.isArray(row?.punch_rows)
          ? row.punch_rows
          : [];
        return;
      }

      const { hasEmbeddedRows, rows } = getEmbeddedPunchRows(row);
      if (hasEmbeddedRows) {
        embeddedPatch[rowKey] = rows;
      } else {
        rowsNeedingFetch.push(row);
      }
    });

    if (Object.keys(embeddedPatch).length) {
      setExpandedPunchData((prev) => ({ ...prev, ...embeddedPatch }));
    }
    rowsNeedingFetch.forEach((row) => {
      getExpandedPunches(row?.user_id, row);
    });
  }, [
    expandMode,
    expandedPunchData,
    getExpandedPunches,
    getTopLevelRowKey,
    isDateWiseView,
    paginatedRows,
  ]);

  const updateExpandMode = useCallback((event, data) => {
    const nextMode = data?.id;
    if (!nextMode) return;

    setExpandMode(nextMode);
    setPersonExpandableUserId(null);
    if (nextMode === "collapse-all") {
      setExpandedUserIds([]);
    }
  }, []);

  const expandControl = useMemo(
    () => (
      <CompactHeaderSelectFilter
        dataList={EXPAND_TOGGLE_OPTIONS}
        selectedValue={
          EXPAND_TOGGLE_OPTIONS.find((option) => option.id === expandMode) ||
          EXPAND_TOGGLE_OPTIONS[0]
        }
        updateDataFunction={updateExpandMode}
        staticLabel=""
        widthSx={{
          width: { xs: "100%", sm: "auto" },
          minWidth: { xs: "100%", sm: 140 },
          maxWidth: { xs: "100%", sm: 140 },
        }}
        buttonSx={{
          minHeight: 40,
          height: 40,
          px: 1,
          borderRadius: 1.5,
          backgroundColor: designColors.white,
          border: `1px solid ${designColors.slate200}`,
          boxShadow: "none",
          color: designColors.slate700,
          "&:hover": {
            backgroundColor: designColors.slateBg,
            border: `1px solid ${designColors.slate300}`,
            boxShadow: "none",
          },
        }}
      />
    ),
    [expandMode, updateExpandMode],
  );

  // Total Punches only means something for Punch Entry — Adjustment Entry's
  // rows don't carry a punch_in_count, so a second card there would just
  // show a misleading zero rather than a genuine summary.
  const punchSummaryCards = useMemo(() => {
    const rows = visiblePunchData || [];
    const cards = [
      {
        key: isDateWiseView ? "dates" : "employees",
        title: isDateWiseView ? "Dates" : "Employees",
        value: rows.length,
        icon: Groups2Rounded,
        accent: "#1D4ED8",
        tint: "#EEF4FF",
      },
    ];

    cards.push({
      key: "totalPunches",
      title: isAdjustmentReport ? "Total Adjustments" : "Total Punches",
      value: rows.reduce(
        (sum, row) => sum + Number(row?.punch_in_count || 0),
        0,
      ),
      icon: PinRounded,
      accent: "#0F766E",
      tint: "#E6F8F4",
    });

    return cards;
  }, [isAdjustmentReport, isDateWiseView, visiblePunchData]);

  const renderExpandedPunchTable = (rowKey) => {
    const expandedRows = expandedPunchData[rowKey] || [];
    const visibleExpandedRows = isAdjustmentReport
      ? expandedRows.filter(isAdjustmentPunchRow)
      : expandedRows;
    const expandedRowsSearch = isDateWiseView
      ? expandedPunchDataSearchByKey[rowKey] || ""
      : "";
    const normalizedExpandedRowsSearch = expandedRowsSearch
      .trim()
      .toLowerCase();
    const filteredExpandedRows = normalizedExpandedRowsSearch
      ? visibleExpandedRows.filter((row) => {
          const name = `${row?.username || ""}`.toLowerCase();
          const employeeId = `${row?.employee_id || ""}`.toLowerCase();
          return (
            name.includes(normalizedExpandedRowsSearch) ||
            employeeId.includes(normalizedExpandedRowsSearch)
          );
        })
      : visibleExpandedRows;
    const expandedRowsPage = expandedPunchDataPageByKey[rowKey] || 0;
    const pagedExpandedRows = filteredExpandedRows.slice(
      expandedRowsPage * PUNCH_DETAIL_PAGE_SIZE,
      expandedRowsPage * PUNCH_DETAIL_PAGE_SIZE + PUNCH_DETAIL_PAGE_SIZE,
    );
    const expandedTableHeight = getExpandedTableHeight(
      pagedExpandedRows.length,
    );

    return (
      <Mui.TableContainer
        sx={expandedPunchTableContainerSx(expandedTableHeight)}
      >
        {isDateWiseView && visibleExpandedRows.length > 0 ? (
          <Mui.Box
            sx={{
              p: 1.25,
              pb: 0.75,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Mui.TextField
              size="small"
              sx={{ width: 280, maxWidth: "100%" }}
              placeholder="Search by name, id..."
              value={expandedRowsSearch}
              onChange={(event) => {
                const nextValue = event.target.value;
                setExpandedPunchDataSearchByKey((prev) => ({
                  ...prev,
                  [rowKey]: nextValue,
                }));
                setExpandedPunchDataPageByKey((prev) => ({
                  ...prev,
                  [rowKey]: 0,
                }));
              }}
              InputProps={{
                startAdornment: (
                  <Mui.InputAdornment position="start">
                    <SearchRounded
                      sx={{ fontSize: 18, color: designColors.slate500 }}
                    />
                  </Mui.InputAdornment>
                ),
              }}
            />
          </Mui.Box>
        ) : null}
        {expandedPunchLoadingUserIds.includes(rowKey) ? (
          <Mui.Box
            sx={{
              minHeight: 120,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Mui.CircularProgress size={28} sx={{ color: designColors.teal }} />
          </Mui.Box>
        ) : visibleExpandedRows.length === 0 ? (
          <EmptyState
            size={56}
            title={
              isAdjustmentReport
                ? "No Adjustments Available"
                : "No Punches Available"
            }
            subtitle={
              isAdjustmentReport
                ? "There are no available adjustments."
                : "There are no available shifts."
            }
          />
        ) : filteredExpandedRows.length === 0 ? (
          <EmptyState
            size={56}
            title="No Matching Results"
            subtitle="No records match your search."
          />
        ) : (
          <ApproveTable sx={expandedPunchTableSx}>
            <Mui.TableHead>
              <Mui.TableRow>
                {activeExpandedColumns.map((column) => (
                  <Mui.TableCell key={column.header}>
                    {column.header}
                  </Mui.TableCell>
                ))}
              </Mui.TableRow>
            </Mui.TableHead>
            <Mui.TableBody>
              {pagedExpandedRows.map((shiftDetails, index) => (
                <Mui.TableRow key={index}>
                  {activeExpandedColumns.map((column) => (
                    <Mui.TableCell key={column.header}>
                      {column.value(shiftDetails)}
                    </Mui.TableCell>
                  ))}
                </Mui.TableRow>
              ))}
            </Mui.TableBody>
          </ApproveTable>
        )}
        {compactRowPager({
          page: expandedRowsPage,
          pageSize: PUNCH_DETAIL_PAGE_SIZE,
          total: filteredExpandedRows.length,
          itemLabel: isAdjustmentReport ? "adjustments" : "punches",
          onPageChange: (nextPage) =>
            setExpandedPunchDataPageByKey((prev) => ({
              ...prev,
              [rowKey]: nextPage,
            })),
        })}
      </Mui.TableContainer>
    );
  };

  const renderPunchCell = (key, element) => {
    if (isDateWiseView) {
      switch (key) {
        case "date":
          return getDisplayValue(element?.date);
        case "punch_in_count":
          return getDisplayValue(element?.punch_in_count, 0);
        default:
          return null;
      }
    }

    switch (key) {
      case "username":
        return (
          <Mui.Stack direction="row" spacing={1} alignItems="center">
            <Mui.Avatar
              sx={{ width: 28, height: 28 }}
              alt="Remy Sharp"
              src={element?.image_url ?? optionalAvathar}
            />
            <Mui.Typography
              sx={{
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                color: designColors.slate900,
                fontSize: 14,
              }}
            >
              {getDisplayValue(element?.username)}
            </Mui.Typography>
          </Mui.Stack>
        );
      case "employee_id":
        return getDisplayValue(element?.employee_id);
      case "department_name":
        return getDisplayValue(element?.department_name);
      case "punch_in_count":
        return getDisplayValue(element?.punch_in_count, 0);
      default:
        return null;
    }
  };

  const handleSummarizedDownload = useCallback(
    async (
      exportRows,
      metadataOverride,
      filterValues,
      sortRules,
      downloadFormat = "excel",
      onProgress,
    ) => {
      const rows = Array.isArray(exportRows) ? exportRows : [];
      if (!rows.length) {
        showToast("info", "No records are available for export.");
        return;
      }

      const headers = activeFilterHeaders;
      const summaryRow = { rowType: "grand_total" };
      if (isDateWiseView) {
        summaryRow.date = "TOTAL";
        summaryRow.punch_in_count = rows.reduce(
          (sum, row) => sum + Number(row?.punch_in_count || 0),
          0,
        );
      } else {
        headers.forEach((header) => {
          if (header.key === "username") {
            summaryRow[header.key] = "TOTAL";
          } else if (header.type === "number") {
            summaryRow[header.key] = rows.reduce(
              (sum, row) => sum + Number(row?.[header.key] || 0),
              0,
            );
          }
        });
      }

      await triggerDownloadByFormat(
        [...rows, summaryRow],
        headers,
        isAdjustmentReport ? "Adjustment Summary" : "Punch Entry Summary",
        metadataOverride,
        filterValues,
        sortRules,
        downloadFormat,
        onProgress,
      );
    },
    [
      activeFilterHeaders,
      isAdjustmentReport,
      isDateWiseView,
      triggerDownloadByFormat,
    ],
  );

  const handleDetailedDownload = useCallback(
    async (
      exportRows,
      metadataOverride,
      _filterValues,
      _sortRules,
      downloadFormat = "excel",
      onProgress,
    ) => {
      const rows = Array.isArray(exportRows) ? exportRows : [];
      const detailedRows = rows.flatMap((row) =>
        isDateWiseView
          ? row?.punch_rows || []
          : getEmbeddedPunchRows(row).rows || [],
      );
      const filteredDetailRows = isAdjustmentReport
        ? detailedRows.filter(isAdjustmentPunchRow)
        : detailedRows;

      if (!filteredDetailRows.length) {
        showToast("info", "No detailed records are available for export.");
        return;
      }

      const detailHeaders = activeExpandedColumns.map((column) => ({
        name: column.header,
        key: column.header,
        customFormatter: column.value,
      }));

      const groups = rows.map((row) => ({
        summaryRow: row,
        detailRows: isDateWiseView
          ? row?.punch_rows || []
          : getEmbeddedPunchRows(row).rows || [],
      }));

      const exportGroupedByFormat =
        downloadFormat === "pdf"
          ? downloadGroupedPdf
          : downloadFormat === "csv"
            ? downloadGroupedCsv
            : downloadGroupedExcel;

      await exportGroupedByFormat(
        groups,
        activeFilterHeaders,
        detailHeaders,
        isAdjustmentReport ? "Adjustment Detailed" : "Punch Entry Detailed",
        metadataOverride,
        onProgress,
        // Punch/Adjustment Detailed always downloads landscape — the
        // per-punch detail rows read poorly portrait even when the identity
        // columns alone would otherwise fit.
        "landscape",
      );
    },
    [
      activeExpandedColumns,
      activeFilterHeaders,
      downloadGroupedCsv,
      downloadGroupedExcel,
      downloadGroupedPdf,
      isAdjustmentReport,
      isDateWiseView,
    ],
  );

  return (
    <ThemeProvider theme={withDesignFont(theme)}>
      <FilterProvider
        domain={domain}
        userDetails={userDetails}
        subModuleId={subModuleId}
      >
        <Box sx={{ pb: 0, width: "100%", maxWidth: "100%" }}>
          <Mui.Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(2, minmax(0, 1fr))",
              },
              "@media (max-width:599.95px)": {
                "& > :last-child:nth-child(odd)": { gridColumn: "1 / -1" },
              },
              gap: 1.5,
              mb: 2,
            }}
          >
            {punchSummaryCards.map((card) => (
              <StatCard
                key={card.key}
                icon={card.icon}
                title={card.title}
                value={card.value}
                accent={card.accent}
                tint={card.tint}
              />
            ))}
          </Mui.Box>

          <Box ref={customFilterRef} sx={approveStickyCustomFilterBar}>
            <CustomFilter
              key={`punch-filter-${viewMode}-${isAdjustmentReport ? "adjustment" : "punch"}`}
              title={reportTitle}
              titleOverride={activeTitleOverride}
              filterHeaders={activeFilterHeaders}
              data={sourceRows}
              filteredData={visiblePunchData}
              filteredDataReady={punchData !== null && punchDataReady}
              onFilteredDataChange={handleFilteredDataChange}
              screenAccess={check_access}
              hideCount={hideCount}
              isAttendanceScreenReport={true}
              showDownloadOptions={true}
              showDownloadTypeSelector={true}
              downloadOptionStates={{
                summarized: {
                  disabled: !hasSummarizedDownloadData,
                  reason: "No summarized report data is available to export.",
                },
                detailed: {
                  disabled: !hasDetailedDownloadData,
                  reason: "No detailed report data is available to export.",
                },
              }}
              downloadDisabledReason="No report data is available to export."
              includeMetadata={true}
              metadata={metadata}
              customSummarizedDownloadHandler={handleSummarizedDownload}
              customDetailedDownloadHandler={handleDetailedDownload}
              downloadFormats={["excel", "pdf", "csv"]}
              getDetailedHeaders={() =>
                activeExpandedColumns.map((column) => ({
                  name: column.header,
                  key: column.header,
                  customFormatter: column.value,
                }))
              }
              pdfRowCountOverride={detailedExportRowCount}
              labeledToolbar={true}
              searchPlaceholder={
                isDateWiseView
                  ? "Search by date..."
                  : "Search by employee, department..."
              }
              newButtonComponent={expandControl}
            />
          </Box>

          <Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
            <Mui.Box>
              <Mui.TableContainer
                sx={{
                  boxSizing: "border-box",
                  width: "100%",
                  maxWidth: "100%",
                  overflowX: { xs: "auto", sm: "visible" },
                  overflowY: { xs: "auto", sm: "visible" },
                  pb: footerHeight ? `${footerHeight}px` : 0,
                  borderLeft: `1px solid ${designColors.slate200}`,
                  borderRight: `1px solid ${designColors.slate200}`,
                  borderBottom: `1px solid ${designColors.slate200}`,
                }}
              >
                <ApproveTable stickyHeader sx={{ width: "100%" }}>
                  <Mui.TableHead sx={designShell.tableStickyHead}>
                    <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                      <Mui.TableCell
                        sx={{
                          ...approveTableHeadCell,
                          top: APP_BAR_OFFSET + customFilterHeight,
                          width: 56,
                          borderTopLeftRadius: 0,
                        }}
                      />
                      {activeFilterHeaders.map((header, headerIndex) => (
                        <Mui.TableCell
                          key={header.key}
                          sx={{
                            ...approveTableHeadCell,
                            top: APP_BAR_OFFSET + customFilterHeight,
                            ...(headerIndex === activeFilterHeaders.length - 1
                              ? { borderTopRightRadius: 0 }
                              : {}),
                          }}
                        >
                          {header.name}
                        </Mui.TableCell>
                      ))}
                    </Mui.TableRow>
                  </Mui.TableHead>
                  <Mui.TableBody>
                    {visiblePunchData?.length === 0 ? (
                      <TableEmptyState dataLength={sourceRows} />
                    ) : (
                      paginatedRows.map((element) => {
                        const rowKey = getTopLevelRowKey(element);
                        const isExpanded =
                          expandMode === "expand-all" ||
                          expandedUserIds.includes(rowKey);

                        return (
                          <React.Fragment key={rowKey}>
                            <Mui.TableRow
                              hover
                              sx={{
                                ...designShell.tableRowHover,
                                "&:last-child td": { borderBottom: "none" },
                                "& td": { backgroundColor: designColors.white },
                              }}
                            >
                              <Mui.TableCell
                                sx={approveTableBodyCell}
                                onClick={() => {
                                  if (isExpanded) {
                                    setExpandMode("collapse-all");
                                    setExpandedUserIds((prev) =>
                                      prev.filter(
                                        (expandedId) => expandedId !== rowKey,
                                      ),
                                    );
                                    if (
                                      `${personExpandableUserId}` ===
                                      `${rowKey}`
                                    ) {
                                      setPersonExpandableUserId(null);
                                    }
                                    return;
                                  }
                                  setExpandMode("collapse-all");
                                  setPersonExpandableUserId(rowKey);
                                  setExpandedUserIds((prev) =>
                                    prev.includes(rowKey)
                                      ? prev
                                      : [...prev, rowKey],
                                  );
                                  if (expandedPunchData[rowKey] === undefined) {
                                    getExpandedPunches(
                                      element?.user_id,
                                      element,
                                    );
                                  }
                                }}
                              >
                                <ArrowRightIcon
                                  sx={{
                                    color: designColors.slate500,
                                    cursor: "pointer",
                                    transition: "transform 0.2s ease",
                                    transform: isExpanded
                                      ? "rotate(90deg)"
                                      : "rotate(0deg)",
                                  }}
                                />
                              </Mui.TableCell>
                              {activeFilterHeaders.map((header) => (
                                <Mui.TableCell
                                  key={header.key}
                                  sx={approveTableBodyCell}
                                >
                                  {renderPunchCell(header.key, element)}
                                </Mui.TableCell>
                              ))}
                            </Mui.TableRow>
                            {isExpanded ? (
                              <Mui.TableRow
                                sx={{
                                  "& td": {
                                    backgroundColor: designColors.white,
                                  },
                                }}
                              >
                                <Mui.TableCell
                                  colSpan={1 + activeFilterHeaders.length}
                                >
                                  {renderExpandedPunchTable(rowKey)}
                                </Mui.TableCell>
                              </Mui.TableRow>
                            ) : null}
                          </React.Fragment>
                        );
                      })
                    )}
                  </Mui.TableBody>
                </ApproveTable>
              </Mui.TableContainer>
            </Mui.Box>

            <Mui.Box ref={footerRef} sx={approveStickyFooterBar}>
              {paginationFooter({
                page,
                rowsPerPage,
                total: visiblePunchData?.length || 0,
                itemLabel: "records",
                onPageChange: setLeavePage,
                onRowsPerPageChange: handleChangeRowsPerPage,
                rowsPerPageOptions: isDateWiseView
                  ? DATE_WISE_ROWS_PER_PAGE_OPTIONS
                  : rowsPerPageOptions,
              })}
            </Mui.Box>
          </Box>
        </Box>
      </FilterProvider>
    </ThemeProvider>
  );
};
